import { useState, type FormEvent, type ChangeEvent } from 'react';
import { api, API_BASE, ApiError, getToken } from '../lib/api';
import type { MeResponse } from './AuthProvider';
import { PLAN_META, PLAN_ORDER, priceLabel, type PlanId } from '../lib/plans';

const STEP_KEY = 'ag_stratos.onboarding_step';
// §17.2 — la encuesta de entrada (tipo de negocio → plan) corre ANTES del wizard.
// Marca local para no re-preguntar si el admin recarga a media alta.
const PLAN_KEY = 'ag_stratos.onboarding_plan_done';

interface Props {
  me: MeResponse;
  onComplete: (refreshedMe: MeResponse) => void;
}

/**
 * Wizard de 3 pasos (V11 / Day 4 — Bloque A):
 *   1. Branding del workspace (brand_name, color, logo URL)
 *   2. Primer cliente final (opcional, "Saltar")
 *   3. Primer proyecto (opcional, "Saltar")
 *
 * Cualquier paso puede saltarse. Al terminar (o saltar todo), POST
 * /me/complete-onboarding marca onboarding_completed_at y App.tsx
 * salta al Dashboard normal. El step actual se persiste en localStorage
 * para retomar si el usuario cierra el wizard a la mitad.
 */
export function OnboardingWizard({ me, onComplete }: Props) {
  const initialStep = (() => {
    const raw = Number(localStorage.getItem(STEP_KEY));
    return Number.isFinite(raw) && raw >= 1 && raw <= 3 ? raw : 1;
  })();
  const [step, setStep] = useState<number>(initialStep);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // §17.2 — encuesta de entrada: tipo de negocio → plan. Gate previo al wizard.
  const [planDone, setPlanDone] = useState<boolean>(
    () => localStorage.getItem(PLAN_KEY) === '1',
  );
  // Default al primer preset del selector (Flow). `mixto` ya no es seleccionable
  // en el alta (se arma à la carte desde platform-admin si lo piden).
  const [plan, setPlan] = useState<PlanId>('crm');
  // Alta 2 (c) — novedades de FluidSol: se ofrecen al FINAL del onboarding, desmarcadas
  // (también en Configuración). Se registra la decisión, sea sí o no.
  const [newsOptIn, setNewsOptIn] = useState(false);

  const submitPlan = async () => {
    setSaving(true);
    setError(null);
    try {
      // Setea tenant.plan + enabled_modules derivados (self-serve solo en onboarding).
      await api('/me/tenant/plan', { method: 'PATCH', body: { plan } });
      localStorage.setItem(PLAN_KEY, '1');
      // Ramificación por plan: los pasos del wizard (cliente final, proyecto) son
      // conceptos CRM. Si el plan NO incluye `crm` (Boutique), saltamos el wizard y
      // vamos directo a la app; si incluye `crm` (crm/mixto), continuamos al wizard.
      if (PLAN_META[plan].modules.includes('crm')) {
        setPlanDone(true);
      } else {
        await finish(); // completa onboarding → App.tsx enruta al Dashboard
      }
    } catch (err: unknown) {
      setError((err as Error)?.message ?? 'No se pudo guardar el plan');
    } finally {
      setSaving(false);
    }
  };

  // Paso 1 — branding (pre-llenado con lo que tenemos del tenant)
  const [brandName, setBrandName] = useState(
    me.tenant.branding.brandName ?? me.tenant.name,
  );
  const [primaryColor, setPrimaryColor] = useState(
    me.tenant.branding.primaryColor ?? '#0F172A',
  );
  const [brandLogoUrl, setBrandLogoUrl] = useState(
    me.tenant.branding.brandLogoUrl ?? '',
  );

  // Paso 2 — primer cliente
  const [clientName, setClientName] = useState('');
  const [clientTaxId, setClientTaxId] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);

  // Paso 3 — primer proyecto
  const [projectTitle, setProjectTitle] = useState('');
  const [projectCurrency, setProjectCurrency] = useState('MXN');
  const [projectBudgetHours, setProjectBudgetHours] = useState('');
  const [projectBudgetAmount, setProjectBudgetAmount] = useState('');

  const goNext = (n: number) => {
    localStorage.setItem(STEP_KEY, String(n));
    setStep(n);
  };

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      // Best-effort: si falla, el usuario la decide después en Configuración.
      await api('/legal/marketing-consent', { method: 'PUT', body: { optIn: newsOptIn } }).catch(() => {});
      const refreshed = await api<MeResponse>('/me/complete-onboarding', {
        method: 'POST',
      });
      localStorage.removeItem(STEP_KEY);
      localStorage.removeItem(PLAN_KEY);
      onComplete(refreshed);
    } catch (err: unknown) {
      setError((err as Error)?.message ?? 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  // -------- Paso 1: branding --------
  const submitBranding = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api('/me/tenant/branding', {
        method: 'PATCH',
        body: {
          displayName: brandName.trim(),
          primaryColor: primaryColor,
          logoUrl: brandLogoUrl.trim(),
        },
      });
      goNext(2);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const msg = Array.isArray(err.body?.message)
          ? err.body.message.join(' · ')
          : err.body?.message ?? err.message;
        setError(msg);
      } else {
        setError((err as Error)?.message ?? 'Error al guardar el branding');
      }
    } finally {
      setSaving(false);
    }
  };

  // -------- Paso 2: primer cliente --------
  const submitClient = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      // End-client schema (V0): name + contact_email (no taxId column).
      // El RFC opcional lo guardamos en notes para no perder el dato.
      const created = await api<{ id: string }>('/end-clients', {
        method: 'POST',
        body: {
          name: clientName,
          contactEmail: clientEmail || undefined,
          notes: clientTaxId ? `RFC: ${clientTaxId}` : undefined,
        },
      });
      setCreatedClientId(created.id);
      goNext(3);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const msg = Array.isArray(err.body?.message)
          ? err.body.message.join(' · ')
          : err.body?.message ?? err.message;
        setError(msg);
      } else {
        setError((err as Error)?.message ?? 'Error al crear el cliente');
      }
    } finally {
      setSaving(false);
    }
  };

  // -------- Paso 3: primer proyecto --------
  const submitProject = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      // Projects schema (V0/V2): name + budgetCurrency (no `currency`).
      // Day 5 Block C: budget_hours y budget_amount opcionales.
      await api('/projects', {
        method: 'POST',
        body: {
          name: projectTitle,
          endClientId: createdClientId ?? undefined,
          budgetCurrency: projectCurrency,
          status: 'PLANNING',
          budgetedHours: projectBudgetHours ? Number(projectBudgetHours) : undefined,
          budget: projectBudgetAmount ? Number(projectBudgetAmount) : undefined,
        },
      });
      await finish();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const msg = Array.isArray(err.body?.message)
          ? err.body.message.join(' · ')
          : err.body?.message ?? err.message;
        setError(msg);
      } else {
        setError((err as Error)?.message ?? 'Error al crear el proyecto');
      }
    } finally {
      setSaving(false);
    }
  };

  // §17.2 — Encuesta de entrada (antes del wizard de 3 pasos). El admin elige el
  // tipo de negocio; se fija plan + módulos. Sin esto, el toggle libre de módulos
  // (retirado) dejaba prender GastroGest/CRM gratis → fuga de ingresos.
  if (!planDone) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4 py-10">
        <div className="card-soft w-full max-w-2xl rounded-2xl border border-slate-200/80 bg-white p-10">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Antes de empezar</p>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">¿Qué tipo de negocio tienes?</h2>
          <p className="mt-1 text-sm text-slate-500">
            Elige el plan que mejor describe lo que necesitas. Define qué módulos verás. Podrás mejorarlo después.
          </p>

          <div className="mt-6 space-y-3">
            {PLAN_ORDER.map((id) => {
              const m = PLAN_META[id];
              const selected = plan === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPlan(id)}
                  className={`block w-full rounded-2xl border p-4 text-left transition ${
                    selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{m.label}</span>
                    <span className="text-xs text-slate-500">{priceLabelConIva(id)}</span>
                  </div>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-400">{m.tagline}</p>
                  <p className="mt-1 text-sm text-slate-600">{m.includes}</p>
                </button>
              );
            })}
          </div>

          {!PLAN_META[plan].modules.includes('crm') && (
            <NewsOptIn checked={newsOptIn} onChange={setNewsOptIn} />
          )}

          {error && <div className="alert-over mt-4 rounded-xl px-3 py-2 text-xs">{error}</div>}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={submitPlan}
              disabled={saving}
              className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Continuar'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4 py-10">
      <div className="card-soft w-full max-w-2xl rounded-2xl border border-slate-200/80 bg-white p-10">
        {/* Stepper */}
        <div className="mb-8 flex items-center gap-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex flex-1 items-center gap-2">
              <div
                className={`grid h-8 w-8 place-items-center rounded-full text-xs font-semibold ${
                  n === step
                    ? 'bg-slate-900 text-white'
                    : n < step
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {n}
              </div>
              {n < 3 && (
                <div
                  className={`h-px flex-1 ${
                    n < step ? 'bg-emerald-300' : 'bg-slate-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={submitBranding} className="space-y-5">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                Paso 1 de 3
              </p>
              <h2 className="font-serif text-2xl tracking-tight text-slate-900">
                Personaliza tu marca
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Esto aparecerá en cotizaciones, reportes y el dashboard.
              </p>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Nombre comercial</span>
              <input
                className="input"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                required
              />
            </label>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">Color primario</span>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200"
                  />
                  <input
                    className="input flex-1"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                  />
                </div>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">Logo — opcional</span>
                {brandLogoUrl ? (
                  <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                    <img src={brandLogoUrl} alt="Logo" className="h-12 w-12 rounded object-contain ring-1 ring-slate-200" />
                    <button type="button" onClick={() => setBrandLogoUrl('')}
                      className="text-xs font-medium text-rose-700 hover:underline">
                      Quitar logo
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      // Upload directo a /me/tenant/logo. El user ya está logueado
                      // post-signup así que /me/* es accesible.
                      const form = new FormData();
                      form.append('logo', file);
                      try {
                        const res = await fetch(`${API_BASE}/me/tenant/logo`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${getToken()}` },
                          body: form,
                        });
                        const data = await res.json();
                        if (!res.ok) {
                          setError(data?.message ?? 'No se pudo subir el logo');
                          return;
                        }
                        setBrandLogoUrl(data.logoUrl);
                      } catch (err) {
                        setError((err as Error)?.message ?? 'No se pudo subir el logo');
                      }
                    }}
                    className="w-full text-xs file:mr-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
                  />
                )}
                <span className="mt-1 block text-[11px] text-slate-400">PNG/JPG, máx 1MB.</span>
              </label>
            </div>

            {error && (
              <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => goNext(2)}
                className="text-xs font-medium text-slate-400 hover:text-slate-700"
              >
                Saltar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Continuar'}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitClient} className="space-y-5">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                Paso 2 de 3
              </p>
              <h2 className="font-serif text-2xl tracking-tight text-slate-900">
                Tu primer cliente final
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Empieza con uno — podrás agregar más después.
              </p>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Nombre del cliente</span>
              <input
                className="input"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
                minLength={2}
                placeholder="Mi Cliente S.A. de C.V."
              />
            </label>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">RFC — opcional</span>
                <input
                  className="input"
                  value={clientTaxId}
                  onChange={(e) => setClientTaxId(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">Email de contacto</span>
                <input
                  className="input"
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                />
              </label>
            </div>

            {error && (
              <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => goNext(3)}
                className="text-xs font-medium text-slate-400 hover:text-slate-700"
              >
                Saltar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Creando…' : 'Crear y continuar'}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={submitProject} className="space-y-5">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                Paso 3 de 3
              </p>
              <h2 className="font-serif text-2xl tracking-tight text-slate-900">
                Tu primer proyecto
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Asocia el cliente que acabas de crear y define la moneda.
              </p>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Título del proyecto</span>
              <input
                className="input"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                required
                minLength={3}
                placeholder="Diagnóstico inicial"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Moneda</span>
              <select
                className="input"
                value={projectCurrency}
                onChange={(e) => setProjectCurrency(e.target.value)}
              >
                <option value="MXN">MXN — Peso mexicano</option>
                <option value="USD">USD — Dólar</option>
                <option value="EUR">EUR — Euro</option>
              </select>
            </label>

            {/* Day 5 Block C: budget hours/amount opcionales en el wizard */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">Horas presupuestadas — opcional</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="1"
                  value={projectBudgetHours}
                  onChange={(e) => setProjectBudgetHours(e.target.value)}
                  placeholder="160"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs text-slate-600">Monto del presupuesto — opcional</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="100"
                  value={projectBudgetAmount}
                  onChange={(e) => setProjectBudgetAmount(e.target.value)}
                  placeholder="50000"
                />
              </label>
            </div>

            {error && (
              <div className="alert-over rounded-xl px-3 py-2 text-xs">{error}</div>
            )}

            <NewsOptIn checked={newsOptIn} onChange={setNewsOptIn} />

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={finish}
                disabled={saving}
                className="text-xs font-medium text-slate-400 hover:text-slate-700"
              >
                Saltar y terminar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Creando…' : 'Crear proyecto'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Alta 2 (d) — los precios del onboarding son antes de IVA. */
function priceLabelConIva(id: PlanId): string {
  return PLAN_META[id].priceMxn == null ? priceLabel(id) : `${priceLabel(id)} + IVA`;
}

/** Alta 2 (c) — casilla de novedades al final del onboarding (NO premarcada). */
function NewsOptIn({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="mt-5 flex items-start gap-2.5 text-[12px] leading-relaxed text-slate-600">
      <input type="checkbox" name="newsOptIn"
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
        checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>Quiero recibir novedades de FluidSol por correo. Puedes cambiarlo cuando quieras en Configuración.</span>
    </label>
  );
}
